import { useState } from "react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, Upload, X, ShieldCheck, Camera, Sun, Shirt, Check, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import PhoneField from "../components/PhoneField";

const MAX_FILE_BYTES = 25 * 1024 * 1024;

async function uploadApplicantPhoto(file) {
  if (file.size > MAX_FILE_BYTES) throw new Error(`${file.name} is over 25 MB`);
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/applications/upload", fd);
  return r.data.url;
}

export default function Scouting() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", age: "", gender: "women",
    city: "", country: "", height_cm: "", instagram: "",
  });
  const [photos, setPhotos] = useState([]);  // [{ url, preview, name, uploading }]
  const [done, setDone] = useState(false);
  const [reference, setReference] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (photos.length + files.length > 5) { toast.error("5 photos maximum"); return; }
    for (const f of files) {
      if (f.size > MAX_FILE_BYTES) { toast.error(`${f.name} is over 25 MB`); continue; }
      const localPreview = URL.createObjectURL(f);
      setPhotos((p) => [...p, { url: null, preview: localPreview, name: f.name, uploading: true }]);
      try {
        const url = await uploadApplicantPhoto(f);
        setPhotos((p) => p.map((t) => (t.preview === localPreview ? { ...t, url, uploading: false } : t)));
      } catch (err) {
        toast.error(err?.response?.data?.detail || err.message || `Failed: ${f.name}`);
        setPhotos((p) => p.filter((t) => t.preview !== localPreview));
        URL.revokeObjectURL(localPreview);
      }
    }
  };

  const removePhoto = (i) => {
    const tile = photos[i];
    if (tile?.preview) URL.revokeObjectURL(tile.preview);
    setPhotos(photos.filter((_, j) => j !== i));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.age || !form.city || !form.country) {
      toast.error("Please complete name, email, age and location"); return;
    }
    const ready = photos.filter((p) => p.url && !p.uploading);
    if (ready.length < 4) { toast.error("Please upload at least 4 photos as instructed"); return; }
    if (photos.some((p) => p.uploading)) { toast.error("Please wait for photos to finish uploading"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/applications", {
        ...form,
        age: Number(form.age),
        height_cm: form.height_cm ? Number(form.height_cm) : null,
        photos: ready.map((p) => p.url),
        biography: "(via /scouting funnel)",
      });
      setReference(r.data.reference);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="pt-32 pb-24 max-w-[900px] mx-auto px-6 lg:px-12">
        <p className="label-overline text-zinc-500 inline-flex items-center gap-2"><ShieldCheck size={12}/> Received</p>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tighter font-light mt-4 leading-[0.9]">
          Thank you<span className="italic text-zinc-600">.</span>
        </h1>
        <p className="text-zinc-700 mt-6 max-w-2xl leading-relaxed">
          Your digitals are with our scouts. If your profile is right for the roster you'll
          hear from us within 7 working days. We answer every submission.
        </p>
        <div className="mt-10 border border-black/15 bg-[#FAFAFA] p-8 max-w-xl">
          <p className="label-overline text-zinc-500">Your reference</p>
          <p className="font-mono text-2xl mt-2 tracking-wider">{reference}</p>
          <p className="text-sm text-zinc-600 mt-3">
            Save this code. Track progress on the <Link to="/status" className="link-underline">status page</Link>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="scouting-page" className="pt-32 pb-16">
      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <p className="label-overline text-zinc-500 inline-flex items-center gap-2">
          <ShieldCheck size={12}/> Scouting · By invitation
        </p>
        <h1 className="font-serif text-6xl md:text-7xl lg:text-8xl tracking-tighter font-light mt-4 leading-[0.85]">
          You were<br/><span className="italic text-zinc-600">found.</span>
        </h1>
        <p className="text-zinc-700 mt-8 max-w-2xl text-lg leading-relaxed">
          Our scouting team has reached out to you because your presence caught our eye. This page
          exists to move the conversation off social media — securely and professionally — and to
          give us what we need to evaluate the fit.
        </p>
      </section>

      {/* DIRECTIVES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-20 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border border-black/10 bg-[#FAFAFA] p-8">
          <p className="label-overline text-zinc-500">Step 01</p>
          <h2 className="font-serif text-2xl mt-2">What we need from you</h2>
          <ul className="mt-5 space-y-3 text-zinc-700">
            <li className="flex gap-3"><Sun size={18} className="shrink-0 text-zinc-700 mt-1"/> Natural-light <b>digitals</b> only — phone camera is fine.</li>
            <li className="flex gap-3"><Camera size={18} className="shrink-0 text-zinc-700 mt-1"/> No filters, no professional retouching, no presets.</li>
            <li className="flex gap-3"><Shirt size={18} className="shrink-0 text-zinc-700 mt-1"/> <b>Form-fitting basic clothing</b> (tank, jeans, plain dress).</li>
            <li className="flex gap-3"><Check size={18} className="shrink-0 text-zinc-700 mt-1"/> <b>No makeup</b>, hair pulled back from the face.</li>
            <li className="flex gap-3"><Check size={18} className="shrink-0 text-zinc-700 mt-1"/> Plain wall as background. Stand tall, neutral expression.</li>
          </ul>
        </div>
        <div className="border border-black/10 bg-[#FAFAFA] p-8">
          <p className="label-overline text-zinc-500">Step 02</p>
          <h2 className="font-serif text-2xl mt-2">Five photos, in this order</h2>
          <ol className="mt-5 space-y-3 text-zinc-700 list-decimal list-inside">
            <li><b>Front face</b> — eyes to camera, neutral.</li>
            <li><b>Side profile</b> — left or right.</li>
            <li><b>Three-quarter</b> — slight angle to camera.</li>
            <li><b>Full body — front</b> — head to toe.</li>
            <li><b>Full body — back</b> — head to toe.</li>
          </ol>
        </div>
      </section>

      {/* FORM */}
      <section className="max-w-[1100px] mx-auto px-6 lg:px-12 mt-24">
        <h2 className="font-serif text-4xl md:text-5xl tracking-tighter font-light leading-[0.9]">
          Submit your <span className="italic">digitals</span>.
        </h2>
        <p className="text-zinc-600 mt-3 max-w-xl">
          Everything stays inside the agency. We never re-share, sell, or post your images.
        </p>

        <form onSubmit={submit} className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Full name *">
            <Input data-testid="scout-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
          </Field>
          <Field label="Email *">
            <Input data-testid="scout-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
          </Field>
          <Field label="WhatsApp / phone">
            <PhoneField value={form.phone} onChange={(v) => set("phone", v)} testId="scout-phone"/>
          </Field>
          <Field label="Age *">
            <Input data-testid="scout-age" type="number" value={form.age} onChange={(e) => set("age", e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
          </Field>
          <Field label="Division *">
            <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
              <SelectTrigger data-testid="scout-gender" className="bg-transparent border-black/20 rounded-none focus:ring-0 focus:border-black">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="women">Women</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Height (cm)">
            <Input data-testid="scout-height" type="number" value={form.height_cm} onChange={(e) => set("height_cm", e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
          </Field>
          <Field label="Instagram">
            <Input data-testid="scout-ig" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} placeholder="@handle" className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
          </Field>
          <Field label="City *">
            <Input data-testid="scout-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
          </Field>
          <Field label="Country *">
            <Input data-testid="scout-country" value={form.country} onChange={(e) => set("country", e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
          </Field>

          <div className="sm:col-span-2 mt-4">
            <Label className="label-overline text-zinc-700">Five digitals *</Label>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-3">
              {photos.map((p, i) => (
                <div key={i} className="relative aspect-[3/4] bg-[#F5F5F5] overflow-hidden">
                  <img src={p.preview || p.url} alt={p.name} className="w-full h-full object-cover"/>
                  {p.uploading && (
                    <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white">
                      <Loader2 size={16} className="animate-spin"/>
                      <span className="label-overline text-[9px] mt-1">Uploading…</span>
                    </div>
                  )}
                  <button type="button" onClick={() => removePhoto(i)} data-testid={`scout-remove-photo-${i}`} className="absolute top-1 right-1 bg-black/70 text-white p-1">
                    <X size={14}/>
                  </button>
                  <span className="absolute top-1 left-1 bg-white text-black text-[10px] px-1.5 py-0.5 font-mono">{i + 1}</span>
                </div>
              ))}
              {photos.length < 5 && (
                <label data-testid="scout-upload-label" className="aspect-[3/4] border border-dashed border-black/30 flex flex-col items-center justify-center text-zinc-600 hover:text-black hover:border-black cursor-pointer transition-colors">
                  <Upload size={20}/>
                  <span className="label-overline mt-2">{photos.length === 0 ? "Add photos" : `+${5 - photos.length} more`}</span>
                  <input data-testid="scout-upload" type="file" accept="image/*" multiple className="hidden" onChange={handleFiles}/>
                </label>
              )}
            </div>
          </div>

          <div className="sm:col-span-2 mt-6 border-t border-black/10 pt-6 flex items-center justify-between flex-wrap gap-4">
            <p className="text-xs text-zinc-500 max-w-md">
              By submitting you confirm you are at least 18, or that you have parental consent.
              We do not share your images outside the agency. <Link to="/privacy" className="link-underline">Privacy policy</Link>.
            </p>
            <button type="submit" disabled={submitting} data-testid="scout-submit" className="btn-primary">
              {submitting && <Loader2 size={14} className="animate-spin"/>} Submit digitals <ArrowUpRight size={16}/>
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-overline text-zinc-700">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
